import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMe, startGoogleLogin, unlock } from "../lib/api";
import CredentialForm from "../components/CredentialForm";
import SwirlingMass from "../components/SwirlingMass";

export default function Gate() {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showOperator, setShowOperator] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    fetchMe()
      .then(() => nav("/interview", { replace: true }))
      .catch(() => {});
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setErr("");
    try {
      await unlock(code.trim());
      nav("/interview");
    } catch {
      setErr("Passcode rejected.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="passcode-gate-container"
      className="grain scan relative flex h-full w-full items-center overflow-hidden bg-[var(--void)]"
    >
      <SwirlingMass density={8000} intensity={0.75} />

      <div
        className="absolute inset-0 z-10"
        style={{
          background:
            "linear-gradient(100deg, #050505 0%, #050505 26%, rgba(5,5,5,0.82) 42%, rgba(5,5,5,0.1) 72%, transparent 100%)",
        }}
      />

      <div className="relative z-20 w-full max-w-xl px-8 sm:px-16 lg:pl-28">
        <p className="label tick-in" style={{ animationDelay: "60ms" }}>
          Covalence&nbsp;/&nbsp;Sphere&nbsp;·&nbsp;Spec 1.0.0
        </p>

        <h1
          className="tick-in mt-6 font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl"
          style={{ animationDelay: "160ms" }}
        >
          The hierarchy was never
          <br />
          <span className="italic text-zinc-400">a staircase.</span>
        </h1>

        <div className="hair tick-in my-8 max-w-xs" style={{ animationDelay: "260ms" }} />

        <p
          className="tick-in max-w-md text-sm leading-relaxed text-zinc-400"
          style={{ animationDelay: "340ms" }}
        >
          The hierarchy assumed humans were one-dimensional — one need at a time, satisfied in
          order, as though nobody ever chased meaning on an empty stomach or risked their safety
          for standing. Needs sit at the core, not the bottom, and every outer shell is pierced by
          lines of force that dive straight back through it. This instrument finds those lines in
          you.
        </p>

        <div className="tick-in mt-12 max-w-sm" style={{ animationDelay: "440ms" }}>
          <button
            onClick={startGoogleLogin}
            data-testid="google-login-btn"
            className="btn-stark w-full justify-center"
          >
            Begin · sign in with Google
          </button>

          {!showEmail ? (
            <button
              onClick={() => setShowEmail(true)}
              data-testid="show-email-auth-btn"
              className="label mt-4 underline decoration-[var(--line-hi)] underline-offset-4 transition-colors hover:text-zinc-200"
            >
              Or use an email address and password →
            </button>
          ) : (
            <CredentialForm onDone={() => nav("/interview", { replace: true })} />
          )}

          <p className="mt-5 font-data text-[10px] leading-relaxed text-zinc-600">
            Your map, your transcript, your results. Nobody else sees them. Either way in, you need
            an invitation from the operator.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2">
            <button
              onClick={() => nav("/privacy")}
              data-testid="gate-privacy-link"
              className="label underline decoration-[var(--line-hi)] underline-offset-4 transition-colors hover:text-zinc-200"
            >
              What we do with your answers →
            </button>
            <button
              onClick={() => nav("/legal")}
              data-testid="gate-legal-link"
              className="label underline decoration-[var(--line-hi)] underline-offset-4 transition-colors hover:text-zinc-200"
            >
              Terms, privacy policy and waiver →
            </button>
          </div>

          {!showOperator ? (
            <button
              onClick={() => setShowOperator(true)}
              data-testid="show-operator-btn"
              className="label mt-8 transition-colors hover:text-zinc-200"
            >
              Operator access →
            </button>
          ) : (
            <form onSubmit={submit} className="mt-8">
              <label className="label block" htmlFor="pc">
                Operator passcode
              </label>
              <div className="mt-3 flex items-stretch border border-[var(--line-hi)]">
                <input
                  id="pc"
                  data-testid="passcode-input"
                  type="password"
                  autoFocus
                  autoComplete="off"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full bg-transparent px-4 py-3 font-data text-sm tracking-[0.3em] text-zinc-100 outline-none placeholder:text-zinc-700"
                />
                <button
                  type="submit"
                  data-testid="passcode-submit-btn"
                  disabled={busy || !code.trim()}
                  className="btn-stark whitespace-nowrap border-0 border-l border-[var(--line-hi)]"
                >
                  {busy ? "Verifying" : "Enter"}
                </button>
              </div>
              {err && (
                <p data-testid="passcode-error-msg" className="mt-3 font-data text-[11px] text-zinc-300">
                  — {err}
                </p>
              )}
            </form>
          )}
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-6 right-6 z-20 text-right">
        <p className="label">Multi-subject · results are private</p>
        <p className="label mt-1">8-hour window · resumable · voice or text</p>
      </div>
    </div>
  );
}
